<template>
  <div>
    <div
      v-if="pageLoading"
      style="display: flex; top: 0px; position: fixed; left: 0px; right: 0px; bottom: 0px; background-color: rgba(247, 247, 247, 0.9); z-index: 9999; justify-content: center; align-items: center;"
    >
      <div>
        <h3>Loading loan officer settings...</h3>
      </div>
    </div>

    <div v-else>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 class="mb-1 font-regular">Loan Officer Settings</h1>
          <p class="text-muted mb-0">
            Manage review officer ranks and collection group assignments without super-admin access.
          </p>
        </div>
      </div>

      <div class="card card--no-shadow mb-4">
        <div class="card-header">
          <h4 class="card-title mb-0">Review Officers — Rank Assignment</h4>
          <small class="text-muted">Lower rank number = higher priority for auto-assignment of new nano-loans.</small>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-striped mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Pending Assigned</th>
                  <th>Rank</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="officer in reviewOfficers" :key="'review-' + officer.id">
                  <td>{{ officer.first_name }} {{ officer.last_name }}</td>
                  <td>{{ officer.email }}</td>
                  <td>
                    <span :class="officer.profile_status === 'active' ? 'text-success' : 'text-danger'">
                      {{ officer.profile_status }}
                    </span>
                  </td>
                  <td>{{ officer.pending_assigned_loans }}</td>
                  <td style="min-width: 140px;">
                    <select
                      class="form-control form-control-sm"
                      v-model="officer.review_officer_rank"
                    >
                      <option :value="null">Unranked</option>
                      <option v-for="rank in 20" :key="rank" :value="rank">Rank {{ rank }}</option>
                    </select>
                  </td>
                  <td>
                    <button
                      class="button btn-sm"
                      :disabled="savingId === 'review-' + officer.id"
                      @click="saveReviewRank(officer)"
                    >
                      {{ savingId === 'review-' + officer.id ? 'Saving...' : 'Save' }}
                    </button>
                  </td>
                </tr>
                <tr v-if="reviewOfficers.length === 0">
                  <td colspan="6" class="text-center text-muted py-4">No review officers found.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="card card--no-shadow">
        <div class="card-header">
          <h4 class="card-title mb-0">Collection Officers — Group Assignment</h4>
          <small class="text-muted">Collection group controls which overdue bucket auto-assignment commands target.</small>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-striped mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Active Assigned</th>
                  <th>Collection Group</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="officer in collectionOfficers" :key="'collection-' + officer.id">
                  <td>{{ officer.first_name }} {{ officer.last_name }}</td>
                  <td>{{ officer.email }}</td>
                  <td>
                    <span :class="officer.profile_status === 'active' ? 'text-success' : 'text-danger'">
                      {{ officer.profile_status }}
                    </span>
                  </td>
                  <td>{{ officer.active_assigned_loans }}</td>
                  <td style="min-width: 160px;">
                    <select
                      class="form-control form-control-sm"
                      v-model="officer.collection_group"
                    >
                      <option value="none">None</option>
                      <option value="DO">DO (D0)</option>
                      <option value="D1">D1</option>
                      <option value="D2">D2</option>
                      <option value="D3">D3</option>
                    </select>
                  </td>
                  <td>
                    <button
                      class="button btn-sm"
                      :disabled="savingId === 'collection-' + officer.id"
                      @click="saveCollectionGroup(officer)"
                    >
                      {{ savingId === 'collection-' + officer.id ? 'Saving...' : 'Save' }}
                    </button>
                  </td>
                </tr>
                <tr v-if="collectionOfficers.length === 0">
                  <td colspan="6" class="text-center text-muted py-4">No collection officers found.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from "@/services/api.service";
import Swal from "sweetalert2";
import ErrorService from "@/services/error.service";

export default {
  name: "LoanOfficerSettings",
  data() {
    return {
      pageLoading: true,
      savingId: null,
      reviewOfficers: [],
      collectionOfficers: []
    };
  },
  methods: {
    async fetchSettings() {
      try {
        this.pageLoading = true;
        const response = await ApiService.get("loan/instant/loan_officer_settings");
        this.reviewOfficers = response.data.data.review_officers || [];
        this.collectionOfficers = response.data.data.collection_officers || [];
      } catch (error) {
        ErrorService.displayErrorAlert(error.response.data);
      } finally {
        this.pageLoading = false;
      }
    },
    async saveReviewRank(officer) {
      this.savingId = "review-" + officer.id;
      try {
        await ApiService.post("loan/instant/update_loan_officer_settings", {
          user_id: officer.id,
          review_officer_rank: officer.review_officer_rank
        });
        Swal.fire({
          title: "Saved",
          text: "Review officer rank updated",
          icon: "success",
          showConfirmButton: false,
          timer: 2000
        });
        await this.fetchSettings();
      } catch (error) {
        ErrorService.displayErrorAlert(error.response.data);
      } finally {
        this.savingId = null;
      }
    },
    async saveCollectionGroup(officer) {
      this.savingId = "collection-" + officer.id;
      try {
        await ApiService.post("loan/instant/update_loan_officer_settings", {
          user_id: officer.id,
          collection_group: officer.collection_group || "none"
        });
        Swal.fire({
          title: "Saved",
          text: "Collection group updated",
          icon: "success",
          showConfirmButton: false,
          timer: 2000
        });
        await this.fetchSettings();
      } catch (error) {
        ErrorService.displayErrorAlert(error.response.data);
      } finally {
        this.savingId = null;
      }
    }
  },
  mounted() {
    this.fetchSettings();
  }
};
</script>
